/**
 * Static configuration for the Per-Week drilldown modal.
 *
 * Extracted out of ``PerWeekDrilldownModal.tsx`` so the component file
 * stays under the 500-line house limit. Holds:
 *   * Drilldown-mode metadata (titles, schedule vs NCR routing).
 *   * Sort-field maps (UI column id → backend ``ordering`` field).
 *   * Column display lists.
 *   * Param builders that compose the filter scope into the query
 *     dictionary used by both the list call and the export call (so the
 *     count shown in the modal == the rows downloaded).
 */

/**
 * Drilldown modes for the Per-Week scheduled-dashboard tiles + chart points.
 *
 * `schedules-*` modes hit `/purchasing/schedules/` and export via
 * `/purchasing/schedules/export-csv/`. `ncrs` mode hits `/quality/ncrs/`.
 *
 * Filter contract per mode (always layered on top of the dashboard scope —
 * customer / supplier / part_number / erp_order_type / exclude_line_zero):
 *
 *  - schedules-otif      schedule_date in [ws,we], schedule_date <= today
 *  - schedules-otd       schedule_date in [ws,we], schedule_date <= today
 *  - schedules-appeals   schedule_date in [ws,we], status__in=shipped,partially_shipped
 *                        (closest schedule-level proxy for delivery-appeal eligibility —
 *                         appeals attach to GRNs, which require received qty)
 *  - schedules-received  schedule_date in [ws,we], status__in=shipped,partially_shipped
 *  - schedules-open      schedule_date in [ws,we], outstanding=true
 *  - schedules-portfolio schedule_date >= today, outstanding=true
 *  - schedules-week      schedule_date in [ws,we] (no segment filter)
 *  - ncrs                ncr_date in [ws,we]
 */
export type PerWeekDrilldownMode =
  | 'schedules-otif'
  | 'schedules-otd'
  | 'schedules-appeals'
  | 'schedules-received'
  | 'schedules-open'
  | 'schedules-portfolio'
  | 'schedules-week'
  | 'ncrs';

/** Dashboard filter scope propagated from the shell. Same shape as the
 *  optional ``PeriodContentProps.filters`` in shared.tsx, but with
 *  ``erp_order_type`` made non-optional since the modal always needs to
 *  pin the scope (caller must default it before passing in). */
export interface DashboardFilterScope {
  customer_id?: number | string;
  supplier_id?: number | string;
  part_number?: string;
  erp_order_type: '1' | '4';
}

export const MODE_TITLES: Record<PerWeekDrilldownMode, string> = {
  'schedules-otif': 'OTIF — Past-due schedules in window',
  'schedules-otd': 'OTD — Past-due schedules in window',
  'schedules-appeals': 'Appeals — Schedules with deliveries in window',
  'schedules-received': 'Received — Schedules delivered against in window',
  'schedules-open': 'Open — Schedules with balance owed in window',
  'schedules-portfolio': 'Portfolio Open — All forward-dated open schedules',
  'schedules-week': 'Week — All schedules due in window',
  'ncrs': 'NCRs raised in window',
};

// ---- Schedule columns ------------------------------------------------------

/** Map UI column id → backend ``ordering`` field. ``null`` means the
 *  column is rendered as a static (non-sortable) header. */
export const SCHEDULE_SORT_FIELDS: Record<string, string | null> = {
  schedule_date: 'schedule_date',
  received_date: null,
  supplier_promise_date: 'supplier_promise_date',
  po_number: 'po_line__po__po_number',
  line_number: 'po_line__line_number',
  schedule_line_number: 'schedule_line_number_int',
  part_number: 'po_line__part_number',
  description: null,
  supplier_name: 'po_line__po__supplier__name',
  schedule_qty: 'schedule_qty',
  shipped_qty: 'shipped_qty',
  balance_qty: 'balance_qty',
  otif_status: null,
};

export interface DrilldownColumnDef {
  id: string;
  label: string;
  align?: 'left' | 'right';
}

export const SCHEDULE_COLUMNS: DrilldownColumnDef[] = [
  { id: 'schedule_date', label: 'Schedule Date', align: 'left' },
  { id: 'received_date', label: 'Received Date', align: 'left' },
  { id: 'supplier_promise_date', label: 'Promise Date', align: 'left' },
  { id: 'po_number', label: 'PO #', align: 'left' },
  { id: 'line_number', label: 'Line', align: 'right' },
  { id: 'schedule_line_number', label: 'Sched #', align: 'right' },
  { id: 'part_number', label: 'Part', align: 'left' },
  { id: 'description', label: 'Description', align: 'left' },
  { id: 'supplier_name', label: 'Supplier', align: 'left' },
  { id: 'schedule_qty', label: 'Sched Qty', align: 'right' },
  { id: 'shipped_qty', label: 'Received Qty', align: 'right' },
  { id: 'balance_qty', label: 'Open Qty', align: 'right' },
  // The OTIF verdict, matching the weekly drilldown. Was labelled 'Status'
  // over a cell rendering the shipping state (active / shipped / received),
  // so the column promised a pass/fail and showed something else entirely.
  { id: 'otif_status', label: 'OTIF Status', align: 'left' },
];

// ---- NCR columns -----------------------------------------------------------

export const NCR_SORT_FIELDS: Record<string, string | null> = {
  ncr_date: 'ncr_date',
  ncr_number: 'ncr_number',
  part_number: 'part_number',
  supplier_name: 'supplier__name',
  reject_qty: null,
  severity: 'severity',
  status: 'status',
  defect: null,
};

export const NCR_COLUMNS: DrilldownColumnDef[] = [
  { id: 'ncr_date', label: 'NCR Date', align: 'left' },
  { id: 'ncr_number', label: 'NCR #', align: 'left' },
  { id: 'part_number', label: 'Part', align: 'left' },
  { id: 'supplier_name', label: 'Supplier', align: 'left' },
  { id: 'reject_qty', label: 'Reject Qty', align: 'right' },
  { id: 'severity', label: 'Severity', align: 'left' },
  { id: 'status', label: 'Status', align: 'left' },
  { id: 'defect', label: 'Defect', align: 'left' },
];

// ---- Param builders --------------------------------------------------------

/** Compose the param dictionary for whichever schedule-mode is active.
 *  Used by both the list call and the export call so the count shown in
 *  the dialog == the rows downloaded by Export. */
/** Status sub-tabs for the OTIF past-due drilldown — slice the same window
 *  by per-schedule otif_status. ``value === ''`` is the All denominator. */
export interface ScheduleStatusTab { label: string; value: string; }

export function getScheduleStatusTabs(mode: PerWeekDrilldownMode): ScheduleStatusTab[] | null {
  if (mode === 'schedules-otif') {
    return [
      { label: 'All', value: '' },
      { label: 'Passed', value: 'passed' },
      { label: 'Pending', value: 'pending' },
      { label: 'Failed', value: 'failed' },
    ];
  }
  return null;
}

export function buildScheduleParams(
  mode: PerWeekDrilldownMode,
  windowStart: string,
  windowEnd: string,
  todayIso: string,
  filters: DashboardFilterScope,
  defaultFirmDays: number,
  otifStatus = '',
): Record<string, string | number | boolean> {
  const p: Record<string, string | number | boolean> = {
    exclude_line_zero: true,
    default_firm_days: defaultFirmDays,
  };
  if (otifStatus) p['otif_status'] = otifStatus;
  if (filters.customer_id) p['po_line__po__customer'] = filters.customer_id;
  if (filters.supplier_id) p['po_line__po__supplier'] = filters.supplier_id;
  if (filters.part_number) p['po_line__part_number'] = filters.part_number;
  p['po_line__po__erp_order_type'] = filters.erp_order_type;

  if (mode === 'schedules-portfolio') {
    p['schedule_date__gte'] = todayIso;
    p['outstanding'] = true;
    return p;
  }

  p['schedule_date__gte'] = windowStart;
  p['schedule_date__lte'] = windowEnd;

  if (mode === 'schedules-otif' || mode === 'schedules-otd') {
    // Past-due schedules: schedule_date <= today AND in the window.
    // Re-cap the upper bound at today so we never include future-dated
    // rows (matches the dashboard SQL `is_past_due` filter).
    const cap = windowEnd < todayIso ? windowEnd : todayIso;
    p['schedule_date__lte'] = cap;
  } else if (mode === 'schedules-received') {
    // Tile counts schedules with shipped_qty > 0 in window. Backend
    // filterset accepts shipped_qty__gt directly so the modal count
    // matches the tile (status proxy was off because partial-shipped
    // rows can sit in 'scheduled' status until the line roll-up runs).
    p['shipped_qty__gt'] = 0;
  } else if (mode === 'schedules-appeals') {
    // Appeals attach to GRNs which require received qty > 0.
    p['shipped_qty__gt'] = 0;
  } else if (mode === 'schedules-open') {
    p['outstanding'] = true;
  }
  return p;
}

/** Compose the param dictionary for the NCR drilldown mode. */
export function buildNcrParams(
  windowStart: string,
  windowEnd: string,
  filters: DashboardFilterScope,
): Record<string, string | number | boolean> {
  const p: Record<string, string | number | boolean> = {
    ncr_date__gte: windowStart,
    ncr_date__lte: windowEnd,
  };
  // Customer scope MUST match the tile — without it the helix-admin path
  // returns NCRs across every customer in the install, while the tile is
  // scoped to the dashboard's resolved customer. Counts diverge by
  // tens/hundreds otherwise. CR-7173: `customer` (not `company`) — the NCR
  // filterset role-routes `company` to the supplier slot for supplier
  // users, which zeroed the supplier's drill-down.
  if (filters.customer_id) p['customer'] = filters.customer_id;
  if (filters.supplier_id) p['supplier'] = filters.supplier_id;
  if (filters.part_number) p['part_number'] = filters.part_number;
  // Scope NCRs through the PO join so the modal matches the tile's
  // scoped_ncrs CTE (which excludes NCRs with no po_line OR linked to a
  // PO of a different order type).
  if (filters.erp_order_type) p['po_line__po__erp_order_type'] = filters.erp_order_type;
  return p;
}
